import { createFileRoute, Link } from "@tanstack/react-router";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ServicesSection } from "@/components/landing/ServicesSection";
import { ExampleAppsSection } from "@/components/landing/ExampleAppsSection";
import { PromotionSection } from "@/components/landing/PromotionSection";
import { IdeaGeneratorFeature } from "@/components/landing/IdeaGeneratorFeature";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";

const PUBLISHED = "2026-08-29T00:00:00+00:00";
const MODIFIED = "2026-09-03T00:00:00+00:00";

const TITLE = "Lead Generation Tools: Calculators & Assessments | Square Migration";
const DESCRIPTION =
  "We build interactive lead generation tools, ROI calculators, assessments, and analyzers, that answer buyer questions and capture qualified leads instead of gated PDFs. Interactive B2B lead magnets that start better sales conversations.";

const FAQS: FaqItem[] = [
  {
    "question": "How long does an interactive lead generation tool take to build?",
    "answer": "Most calculators, assessments and analyzers go live in a few weeks. We scope one clear buyer question, model the logic, then design and ship a branded, mobile-friendly tool hosted on your domain."
  },
  {
    "question": "What kinds of lead generation tools do you build?",
    "answer": "ROI and cost calculators, assessments and scorecards, analyzers and diagnostics, and interactive reports, each built around your pricing, benchmarks or scoring model."
  },
  {
    "question": "Where do the leads go?",
    "answer": "Results and submitted inputs are routed to your CRM or inbox, so sales follows up with the prospect's actual numbers and answers as context."
  },
  {
    "question": "Do you help promote the tool once it's live?",
    "answer": "Yes, as an optional add-on we run managed paid LinkedIn campaigns, audience targeting, creative, budget pacing and lead handoff. You own the tool either way."
  }
];

export const Route = createFileRoute("/lead-generation-tools")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/lead-generation-tools" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/lead-generation-tools" }],
    scripts: [...serviceSchemaScripts({
      path: "/lead-generation-tools",
      name: "Lead Generation Tools",
      description: DESCRIPTION,
      serviceType: "Interactive lead generation tool development",
    }), faqSchemaScript(FAQS), webPageSchemaScript({
      path: "/lead-generation-tools",
      name: TITLE,
      description: DESCRIPTION,
      published: PUBLISHED,
      modified: MODIFIED,
    })],
  }),
  component: LeadGenerationToolsPage,
});

function LeadGenerationToolsPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Lead Generation Tools"
        title="Interactive tools that answer your buyer's question first."
        description="Calculators, assessments, and analyzers that give prospects a real answer in exchange for real contact data. Built fast, branded to you, and easy to promote across email, ads, and sales outreach."
        bullets={["ROI calculators", "Assessments & scorecards", "Analyzers & diagnostics", "Interactive reports"]}
      />
      <ServicesSection
        eyebrow="Tool Formats"
        headline={
          <>
            Three ways to answer a <span className="text-primary">buyer’s question.</span>
          </>
        }
        lead="Most lead-gen tools fall into one of three shapes. Pick the one that matches the decision your buyer is stuck on, and we’ll scope it around your data, your math, and your brand."
      />
      <ExampleAppsSection />
      <PromotionSection
        eyebrow="Optional Add-On"
        headline={
          <>
            Promote your B2B app with a managed{"\n"}
            <span className="text-primary">LinkedIn campaign.</span>
          </>
        }
        lead="Once your tool is live, we can run a targeted paid LinkedIn campaign to put it in front of the exact buyers it was built for. We handle audience targeting, creative, budget pacing, and lead handoff, so the asset you built actually gets used."
        note="This is an optional paid add-on, not a requirement. You own the tool either way; the campaign is there when you want to accelerate qualified traffic."
      />
      <IdeaGeneratorFeature />
      <ProcessSection
        eyebrow="How a Tool Gets Built"
        title="From a buyer question to a live tool in weeks."
        lead="We keep scope tight on purpose: one clear question, one useful answer, and a lead record your sales team can act on the same day."
        steps={[
          { number: "01", title: "Pin the Question", description: "Name the single calculation or decision your prospects keep asking about before they’ll talk to sales." },
          { number: "02", title: "Model the Logic", description: "We translate your pricing, benchmarks or scoring model into inputs and outputs a buyer can trust." },
          { number: "03", title: "Design & Ship", description: "Branded, mobile-friendly, and hosted on your domain, usually live within a few weeks." },
          { number: "04", title: "Route the Leads", description: "Results and submitted inputs land in your CRM or inbox so follow-up starts with real context." },
        ]}
      />
      <ClosingCTA
        eyebrow="Scope a Tool"
        headline={
          <>
            Have a calculation your buyers <span className="text-primary">keep asking about?</span>
          </>
        }
        subhead="That’s the tool."
        body="Bring the question, the math, or even a rough spreadsheet. We’ll tell you in one call whether it makes a strong calculator, an assessment, or something in between."
        ctaLabel="Talk Through Your Tool Idea"
      />
      <p className="mx-auto max-w-4xl px-5 sm:px-6 text-muted-foreground">
        New to the idea? Read our guide to{" "}
        <Link to="/b2b-lead-magnets" className="text-primary hover:underline">B2B lead magnets</Link>{" "}
        and why interactive lead magnets are replacing gated PDFs.
      </p>
      <FaqSection items={FAQS} />
      <RelatedServices current="/lead-generation-tools" />
      <Footer />
    </main>
  );
}
